import {
    computed, type ComputedRef, type Ref, ref,
} from 'vue';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { deltaGptApi } from '@/api/deltaGpt/deltaGptApi.ts';
import { IDocumentComment, TDocumentCommentVote } from '@/interfaces/deltaGpt/IDocumentComment.ts';
import { IRichTextPendingImage } from '@/interfaces/deltaGpt/IRichTextImage.ts';
import { isRichTextHtmlEmpty } from '@/utils/htmlUtils.ts';
import { useDeltaGptStore } from '@/stores/deltaGpt/deltaGptStore.ts';

interface ICommentInput {
    getPendingImages: () => Array<IRichTextPendingImage>;
    clear: () => void;
}

interface IUseDocumentCommentsOptions {
    documentShortCode: string;
    initialCommentCount: number;
    initialComments: Array<IDocumentComment> | null;
    locked: ComputedRef<boolean>;
    canComment: ComputedRef<boolean>;
    isSavingComment: Ref<boolean>;
    newCommentInput: Ref<ICommentInput | null>;
    activeReplyInput: Ref<ICommentInput | null>;
    activeEditInput: Ref<ICommentInput | null>;
    onCommentCountUpdated: (count: number) => void;
    onReplyCreated: () => void;
    onCommentUpdated: () => void;
}

interface IUseDocumentComments {
    comments: Ref<Array<IDocumentComment>>;
    locallyInsertedCommentIds: Ref<Set<string>>;
    isLoading: Ref<boolean>;
    hasLoadedComments: Ref<boolean>;
    deletingCommentId: Ref<string | null>;
    commentCount: ComputedRef<number>;
    initialise: () => Promise<void>;
    refreshComments: () => Promise<void>;
    createComment: (bodyHtml: string, parentCommentId?: string | null) => Promise<void>;
    updateComment: (commentId: string, bodyHtml: string) => Promise<void>;
    voteOnComment: (commentId: string, vote: TDocumentCommentVote) => Promise<void>;
    isCommentVotePending: (commentId: string) => boolean;
    confirmDeleteComment: (commentId: string) => void;
}

export function useDocumentComments (options: IUseDocumentCommentsOptions): IUseDocumentComments {
    const confirm = useConfirm();
    const toast = useToast();
    const deltaGptStore = useDeltaGptStore();
    const comments = ref<Array<IDocumentComment>>([]);
    const isLoading = ref<boolean>(true);
    const hasLoadedComments = ref<boolean>(false);
    const deletingCommentId = ref<string | null>(null);
    const votingCommentIds = ref<Set<string>>(new Set());
    const locallyInsertedCommentIds = ref<Set<string>>(new Set());

    const commentCount = computed<number>(() => {
        if (options.locked.value) return 0;
        if (!hasLoadedComments.value) return options.initialCommentCount;
        return comments.value.reduce((count, comment) => count + 1 + comment.replies.length, 0);
    });

    function notifyCountUpdated (): void {
        const count = commentCount.value;
        deltaGptStore.updateDocumentCommentCount(options.documentShortCode, count);
        options.onCommentCountUpdated(count);
    }

    function setComments (nextComments: Array<IDocumentComment>): void {
        comments.value = nextComments.map(comment => ({ ...comment, replies: [...comment.replies] }));
    }

    async function refreshComments (): Promise<void> {
        if (options.locked.value) {
            comments.value = [];
            hasLoadedComments.value = true;
            return;
        }

        setComments(await deltaGptApi.getDocumentComments(options.documentShortCode));
        locallyInsertedCommentIds.value = new Set();
        hasLoadedComments.value = true;
        notifyCountUpdated();
    }

    async function initialise (): Promise<void> {
        try {
            if (options.locked.value) {
                comments.value = [];
                hasLoadedComments.value = true;
                return;
            }

            if (options.initialComments) {
                setComments(options.initialComments);
                hasLoadedComments.value = true;
                notifyCountUpdated();
                return;
            }

            await refreshComments();
        } finally {
            isLoading.value = false;
        }
    }

    function normalizeVote (vote: TDocumentCommentVote | string | null | undefined): TDocumentCommentVote | null {
        if (vote === 'Up' || vote === 'Down') return vote;
        if (vote?.toLowerCase() === 'up') return 'Up';
        if (vote?.toLowerCase() === 'down') return 'Down';
        return null;
    }

    function normalizeCommentVote (comment: IDocumentComment): IDocumentComment {
        return { ...comment, currentUserVote: normalizeVote(comment.currentUserVote) };
    }

    function normalizeCreatedComment (comment: IDocumentComment): IDocumentComment {
        return { ...normalizeCommentVote(comment), replies: [...comment.replies] };
    }

    function addLocalComment (comment: IDocumentComment, parentCommentId: string | null): void {
        locallyInsertedCommentIds.value = new Set([...locallyInsertedCommentIds.value, comment.commentId]);

        if (!parentCommentId) {
            comments.value = [comment, ...comments.value];
            return;
        }

        comments.value = comments.value.map(existingComment => (
            existingComment.commentId === parentCommentId
                ? { ...existingComment, replies: [comment, ...existingComment.replies] }
                : existingComment
        ));
    }

    async function createComment (bodyHtml: string, parentCommentId: string | null = null): Promise<void> {
        if (!options.canComment.value || isRichTextHtmlEmpty(bodyHtml)) return;

        options.isSavingComment.value = true;
        try {
            const input = parentCommentId ? options.activeReplyInput.value : options.newCommentInput.value;
            const savedComment = normalizeCreatedComment(await deltaGptApi.createDocumentComment(options.documentShortCode, {
                bodyHtml,
                parentCommentId,
                richTextImages: input?.getPendingImages() ?? [],
            }));
            addLocalComment(savedComment, parentCommentId);

            if (parentCommentId) options.onReplyCreated();
            else options.newCommentInput.value?.clear();

            hasLoadedComments.value = true;
            notifyCountUpdated();
        } catch {
            toast.add({
                severity: 'error', summary: 'Error', detail: 'Could not save comment.', life: 5000,
            });
        } finally {
            options.isSavingComment.value = false;
        }
    }

    async function updateComment (commentId: string, bodyHtml: string): Promise<void> {
        if (!options.canComment.value || isRichTextHtmlEmpty(bodyHtml)) return;

        options.isSavingComment.value = true;
        try {
            await deltaGptApi.updateDocumentComment(options.documentShortCode, commentId, {
                bodyHtml,
                richTextImages: options.activeEditInput.value?.getPendingImages() ?? [],
            });
            options.onCommentUpdated();
            await refreshComments();
        } catch {
            toast.add({
                severity: 'error', summary: 'Error', detail: 'Could not update comment.', life: 5000,
            });
        } finally {
            options.isSavingComment.value = false;
        }
    }

    function findComment (commentId: string): IDocumentComment | null {
        return comments.value.find(comment => comment.commentId === commentId)
            ?? comments.value.flatMap(comment => comment.replies).find(reply => reply.commentId === commentId)
            ?? null;
    }

    function getVoteValue (vote: TDocumentCommentVote): number {
        return vote === 'Up' ? 1 : -1;
    }

    function getVoteScoreChange (currentVote: TDocumentCommentVote | null, nextVote: TDocumentCommentVote | null): number {
        if (!currentVote && !nextVote) return 0;
        if (!currentVote && nextVote) return getVoteValue(nextVote);
        if (currentVote && !nextVote) return -getVoteValue(currentVote);
        return getVoteValue(nextVote!) - getVoteValue(currentVote!);
    }

    function getOptimisticVotedComment (comment: IDocumentComment, vote: TDocumentCommentVote): IDocumentComment {
        const currentVote = normalizeVote(comment.currentUserVote);
        const nextVote = currentVote === vote ? null : vote;
        return { ...comment, voteCount: comment.voteCount + getVoteScoreChange(currentVote, nextVote), currentUserVote: nextVote };
    }

    function replaceComment (comment: IDocumentComment, updatedComment: IDocumentComment): IDocumentComment {
        if (comment.commentId === updatedComment.commentId) return { ...updatedComment, replies: comment.replies };
        return {
            ...comment,
            replies: comment.replies.map(reply => (reply.commentId === updatedComment.commentId
                ? { ...updatedComment, replies: reply.replies }
                : reply)),
        };
    }

    function isCommentVotePending (commentId: string): boolean {
        return votingCommentIds.value.has(commentId);
    }

    function setCommentVotePending (commentId: string, isPending: boolean): void {
        const nextPendingCommentIds = new Set(votingCommentIds.value);
        if (isPending) nextPendingCommentIds.add(commentId);
        else nextPendingCommentIds.delete(commentId);
        votingCommentIds.value = nextPendingCommentIds;
    }

    async function voteOnComment (commentId: string, vote: TDocumentCommentVote): Promise<void> {
        const commentToUpdate = findComment(commentId);
        if (!commentToUpdate || isCommentVotePending(commentId)) return;

        const previousComments = comments.value;
        comments.value = comments.value.map(comment => replaceComment(comment, getOptimisticVotedComment(commentToUpdate, vote)));
        setCommentVotePending(commentId, true);

        try {
            const updatedComment = await deltaGptApi.voteDocumentComment(options.documentShortCode, commentId, { vote });
            comments.value = comments.value.map(comment => replaceComment(comment, normalizeCommentVote(updatedComment)));
        } catch {
            comments.value = previousComments;
            toast.add({
                severity: 'error', summary: 'Error', detail: 'Could not save vote.', life: 5000,
            });
        } finally {
            setCommentVotePending(commentId, false);
        }
    }

    async function deleteComment (commentId: string): Promise<void> {
        deletingCommentId.value = commentId;
        try {
            await deltaGptApi.deleteDocumentComment(options.documentShortCode, commentId);
            await refreshComments();
        } catch {
            toast.add({
                severity: 'error', summary: 'Error', detail: 'Could not delete comment.', life: 5000,
            });
        } finally {
            deletingCommentId.value = null;
        }
    }

    function confirmDeleteComment (commentId: string): void {
        confirm.require({
            message: 'Are you sure you would like to delete this comment?',
            accept: () => { deleteComment(commentId); },
        });
    }

    return {
        comments,
        locallyInsertedCommentIds,
        isLoading,
        hasLoadedComments,
        deletingCommentId,
        commentCount,
        initialise,
        refreshComments,
        createComment,
        updateComment,
        voteOnComment,
        isCommentVotePending,
        confirmDeleteComment,
    };
}
